import { MallaCurricularDto } from "../../../dto/malla-curricular.dto";
import { apiClient } from "../../../lib/apiClient";

export interface AnioEscolar {
  idAnioEscolar: number;
  nombre: string;
  orden: number;
}

export interface UnidadContenido {
  idUnidadContenido: number;
  nombre: string;
  orden: number;
}

export interface TemaContenido {
  idTemaContenido: number;
  nombre: string;
  orden: number;
}

export interface GrupoContenido {
  idGrupoContenido: number;
  nombre: string;
  orden: number;
}

export interface ContenidoCurricularResponse {
  idContenido: number;
  anioEscolar: {
    idAnioEscolar: number;
    nombre: string;
  };
  unidadContenido: {
    idUnidadContenido: number;
    nombre: string;
  };
  temaContenido: {
    idTemaContenido: number;
    nombre: string;
  };
  grupoContenido?: {
    idGrupoContenido: number;
    nombre: string;
  };
  conceptosPrevios?: string;
  explicacion?: string;
  ejemplo?: string;
  solucion?: string;
  dificultad?: number;
  aplicacionPractica?: string;
  fecha?: string;
  imagen?: string;
}

export interface ContenidoCurricularApiResponse {
  status: "success" | "error";
  data: ContenidoCurricularResponse[];
  message?: string;
}

export interface ContenidoCurricularSingleApiResponse {
  status: "success" | "error";
  data: ContenidoCurricularResponse;
  message?: string;
}

class MallaCurricularService {
  async getAniosEscolares(): Promise<AnioEscolar[]> {
    try {
      const response = await apiClient.get(
        "/contenido-curricular/anios-escolares"
      );
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async getUnidadesContenido(): Promise<UnidadContenido[]> {
    try {
      const response = await apiClient.get(
        "/contenido-curricular/unidades-contenido"
      );
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async getTemasContenido(): Promise<TemaContenido[]> {
    try {
      const response = await apiClient.get(
        "/contenido-curricular/temas-contenido"
      );
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async getGruposContenido(): Promise<GrupoContenido[]> {
    try {
      const response = await apiClient.get(
        "/contenido-curricular/grupos-contenido"
      );
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async getContenidosCurriculares(): Promise<MallaCurricularDto[]> {
    try {
      const response = await apiClient.get("/contenido-curricular");

      const registrosFiltrados = response.data.data.filter(
        (contenido: ContenidoCurricularResponse) => {
          // Filtrar registros que tengan las relaciones obligatorias
          // grupoContenido es opcional según el esquema de Prisma
          const tieneRelacionesObligatorias =
            contenido.temaContenido &&
            contenido.unidadContenido &&
            contenido.anioEscolar;

          return tieneRelacionesObligatorias;
        }
      );

      return registrosFiltrados.map(
        (contenido: ContenidoCurricularResponse) => ({
          id: contenido.idContenido,
          nombre: contenido.temaContenido?.nombre || "Sin tema",
          descripcion: contenido.explicacion || "Sin descripción",
          nivel: contenido.anioEscolar?.nombre || "Sin año",
          unidad: contenido.unidadContenido?.nombre || "Sin unidad",
          dificultad: contenido.dificultad || 1,
          fechaCreacion: contenido.fecha
            ? new Date(contenido.fecha).toISOString().split("T")[0]
            : "Sin fecha",
        })
      );
    } catch (error: any) {
      console.error("Error detallado:", error);
      console.error("Status:", error.response?.status);
      console.error("Data:", error.response?.data);
      console.error("Message:", error.message);
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async getContenidoCurricularById(
    id: number
  ): Promise<ContenidoCurricularResponse> {
    try {
      const response = await apiClient.get(`/contenido-curricular/${id}`);
      return response.data.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async crearContenidoCurricular(data: {
    anioEscolarId: number;
    unidadContenidoId: number;
    temaContenidoId: number;
    grupoContenidoId?: number;
    conceptosPrevios?: string;
    explicacion?: string;
    ejemplo?: string;
    solucion?: string;
    dificultad?: number;
    aplicacionPractica?: string;
    fecha?: string;
    imagen?: File | undefined;
  }): Promise<ContenidoCurricularResponse> {
    try {
      const formData = new FormData();

      // Agregar campos al FormData, excluyendo imagen si es null
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === "imagen") {
            if (value instanceof File) {
              formData.append(key, value);
            }
            // Si es null, no agregar nada
          } else {
            formData.append(key, String(value));
          }
        }
      });

      const response = await apiClient.post("/contenido-curricular", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.status === "success") {
        return response.data.data;
      } else {
        throw new Error(
          response.data.message || "Error al crear contenido curricular"
        );
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async actualizarContenidoCurricular(
    id: number,
    data: {
      anioEscolarId: number;
      unidadContenidoId: number;
      temaContenidoId: number;
      grupoContenidoId?: number;
      conceptosPrevios?: string;
      explicacion?: string;
      ejemplo?: string;
      solucion?: string;
      dificultad?: number;
      aplicacionPractica?: string;
      fecha?: string;
      imagen?: File | undefined;
    }
  ): Promise<ContenidoCurricularResponse> {
    try {
      const formData = new FormData();

      // Agregar campos al FormData, excluyendo imagen si es undefined
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === "imagen") {
            if (value instanceof File) {
              formData.append(key, value);
            }
            // Si es undefined, no agregar nada
          } else {
            formData.append(key, String(value));
          }
        }
      });

      const response = await apiClient.put(
        `/contenido-curricular/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status === "success") {
        return response.data.data;
      } else {
        throw new Error(
          response.data.message || "Error al actualizar contenido curricular"
        );
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }

  async eliminarContenidoCurricular(id: number): Promise<void> {
    try {
      await apiClient.delete(`/contenido-curricular/${id}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || "Error de conexión");
    }
  }
}

export const mallaCurricularService = new MallaCurricularService();
